/**
 * Prepara una foto de cédula antes de subirla a verify_identity.
 *
 * El backend (AWS Textract) solo acepta JPEG/PNG y archivos de máx. 5 MB, así que:
 * - JPEG/PNG livianos y de tamaño razonable se envían tal cual.
 * - Fotos pesadas (> ~2,5 MB) o muy grandes (> 2400 px) se reducen a 2000 px
 *   en el lado largo y se re-codifican como JPEG (calidad ~0,85).
 * - WebP/HEIC (u otros formatos) se convierten a JPEG si el navegador puede
 *   decodificarlos (Safari abre HEIC); si no, se rechazan con un mensaje claro,
 *   porque el backend los rechazaría igual.
 */

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const COMPRESS_THRESHOLD_BYTES = 2.5 * 1024 * 1024;
const MAX_DIMENSION_BEFORE_RESIZE = 2400;
const TARGET_LONG_SIDE = 2000;
const JPEG_QUALITY = 0.85;
const JPEG_QUALITY_FALLBACK = 0.7;

const BACKEND_TYPES = ["image/jpeg", "image/jpg", "image/png"];

export class IdentityImageError extends Error {}

function isHeic(file) {
  return (
    /image\/hei(c|f)/i.test(file.type || "") ||
    /\.(heic|heif)$/i.test(file.name || "")
  );
}

function looksLikeImage(file) {
  return (file.type || "").startsWith("image/") || isHeic(file);
}

function baseName(name) {
  const clean = String(name || "cedula").replace(/\.[^.]+$/, "");
  return clean || "cedula";
}

function decodeImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (!img.naturalWidth || !img.naturalHeight) {
        reject(new Error("empty image"));
        return;
      }
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode failed"));
    };
    img.src = url;
  });
}

function canvasToJpeg(canvas, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}

async function reencodeAsJpeg(img, originalName) {
  const longSide = Math.max(img.naturalWidth, img.naturalHeight);
  const scale = longSide > TARGET_LONG_SIDE ? TARGET_LONG_SIDE / longSide : 1;
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  // Fondo blanco: los PNG con transparencia quedarían negros en JPEG.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  let blob = await canvasToJpeg(canvas, JPEG_QUALITY);
  if (blob && blob.size > MAX_UPLOAD_BYTES) {
    blob = await canvasToJpeg(canvas, JPEG_QUALITY_FALLBACK);
  }
  if (!blob) return null;

  return new File([blob], `${baseName(originalName)}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

/**
 * @param {File} file
 * @returns {Promise<File>} archivo listo para subir (JPEG/PNG, ≤ 5 MB)
 * @throws {IdentityImageError} con un mensaje para mostrar al usuario
 */
export async function prepareIdentityImage(file) {
  if (!file) throw new IdentityImageError("Selecciona una foto de tu cédula.");
  if (!looksLikeImage(file)) {
    throw new IdentityImageError(
      "El archivo no es una imagen. Sube una foto de tu cédula en JPG o PNG."
    );
  }

  const backendReady = BACKEND_TYPES.includes((file.type || "").toLowerCase());

  let img = null;
  try {
    img = await decodeImage(file);
  } catch {
    img = null;
  }

  if (!img) {
    if (isHeic(file) || !backendReady) {
      throw new IdentityImageError(
        "Tu navegador no puede procesar este formato de foto (por ejemplo HEIC). " +
          "Toma la foto de nuevo o súbela en JPG o PNG."
      );
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new IdentityImageError(
        "La foto pesa más de 5 MB. Toma una foto con menor resolución o súbela en JPG."
      );
    }
    return file;
  }

  const tooBig =
    file.size > COMPRESS_THRESHOLD_BYTES ||
    Math.max(img.naturalWidth, img.naturalHeight) > MAX_DIMENSION_BEFORE_RESIZE;

  if (backendReady && !tooBig) return file;

  let prepared = null;
  try {
    prepared = await reencodeAsJpeg(img, file.name);
  } catch {
    prepared = null;
  }

  if (!prepared) {
    if (backendReady && file.size <= MAX_UPLOAD_BYTES) return file;
    throw new IdentityImageError(
      "No pudimos procesar la foto. Intenta con otra foto en JPG o PNG (máx. 5 MB)."
    );
  }

  // Si por alguna razón la versión comprimida pesa más, conservar el original válido.
  if (backendReady && file.size <= MAX_UPLOAD_BYTES && prepared.size >= file.size) {
    return file;
  }

  if (prepared.size > MAX_UPLOAD_BYTES) {
    throw new IdentityImageError(
      "La foto pesa más de 5 MB incluso después de comprimirla. Intenta con otra foto."
    );
  }

  return prepared;
}

